import { MouseEventHandler } from 'react';
import { Checkmark, ChevronDown, IconProps } from '@drift-labs/icons';
import { SelectContentProps, SelectItemProps } from '@radix-ui/react-select';
import { Z_INDEX } from 'src/constants/zIndex';
import { cm } from 'src/utils/classNames';
import { WithChildren, WithClassName, WithRef } from 'src/utils/UtilityTypes';

export interface DropdownUiTriggerProps extends WithClassName, WithChildren {
	size?: 'sm' | 'md';
	label?: string;
	disabled?: boolean;
	labelClassName?: string;
	valueClassName?: string;
	onClick?: MouseEventHandler<HTMLButtonElement>;
	style?: React.CSSProperties;
}

function Trigger({
	size = 'sm',
	className,
	labelClassName,
	valueClassName,
	label,
	disabled,
	children,
	...rest
}: WithRef<DropdownUiTriggerProps, HTMLButtonElement>) {
	return (
		<button
			className={cm(
				'group/dropdown-trigger flex items-center justify-between gap-x-sm',
				'rounded-sm border border-input-border bg-input-bg transition-colors',
				!disabled
					? 'bg-input-bg text-text-secondary hover:bg-button-disabled hover:text-text-default'
					: 'bg-button-disabled text-text-disabled',
				'whitespace-nowrap data-[state=open]:text-text-default',
				size === 'sm' ? 'px-md py-sm' : 'px-lg py-md',
				className
			)}
			disabled={disabled}
			{...rest}
		>
			{label && (
				<span className={cm('typo-b4 transition-colors', labelClassName)}>
					{label}
				</span>
			)}
			<div
				className={cm(
					'flex min-w-0 items-center gap-x-xs typo-t3 capitalize only:w-full only:justify-between',
					!disabled ? 'text-text-default' : 'text-text-disabled',
					valueClassName
				)}
			>
				{children}
			</div>
		</button>
	);
}

export type DropdownUiContentProps = WithChildren<
	WithClassName<SelectContentProps>
>;

function Content({ className, ...rest }: DropdownUiContentProps) {
	return (
		<div
			className={cm(
				'overflow-hidden rounded-sm border border-input-border bg-container-bg shadow-lg',
				Z_INDEX.dropdown,
				className
			)}
			{...rest}
		/>
	);
}

export interface SelectOptionProps extends Pick<
	SelectItemProps,
	'children' | 'className' | 'disabled' | 'style'
> {
	isSelected?: boolean;
}

function Item({
	className,
	children,
	isSelected,
	disabled,
	...rest
}: WithRef<SelectOptionProps, HTMLDivElement>) {
	return (
		<div
			className={cm(
				'flex cursor-pointer items-center justify-between gap-x-sm bg-container-bg p-md select-none',
				'typo-b4 text-text-default',
				!disabled && [
					'hover:bg-button-secondary-bg',
					// Apply when items is navigated to with keyboard for `Select`.
					'data-highlighted:bg-button-secondary-bg',
					// Apply when items is navigated to with keyboard for `Combobox`.
					'data-[selected=true]:bg-button-secondary-bg',
					// Apply when items is navigated to with keyboard for `MultiSelect`.
					'data-[active-item]:bg-button-secondary-bg',
					// Apply styles when items is selected for `Select`.
					'data-[state=checked]:font-semibold data-[state=checked]:text-purple-link',
					// Apply styles when items is selected for `Combobox`.
					isSelected && 'typo-t4 text-purple-link',
				],
				disabled && 'cursor-not-allowed bg-button-disabled text-text-disabled',
				className
			)}
			{...rest}
		>
			{children}
		</div>
	);
}

function Chevron({ size = 16, ...rest }: IconProps) {
	return (
		<ChevronDown
			className="shrink-0 transition-transform group-data-[state=open]/dropdown-trigger:rotate-180"
			size={size}
			{...rest}
		/>
	);
}

function SelectedCheckmark({ size = 16, ...rest }: IconProps) {
	return <Checkmark size={size} {...rest} />;
}

export default { Trigger, Content, Item, Chevron, SelectedCheckmark };
